/**
 * =============================================================================
 * Componente: ReferralCreditHistoryModal
 * Versão: 1.0.0 (2026-05-04)
 *
 * Descrição:
 *   Modal premium com o Histórico de Consumo de Créditos de Conversão do
 *   módulo Indicação. Exibe ledger paginado com filtros de período e tipo
 *   (consumo / estorno / outros). Acesso restrito a admin do tenant
 *   (revalidado no backend pela RPC `get_referral_credit_history`).
 *
 *   Mobile: fullscreen (padrão Lovable).
 * =============================================================================
 */

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight, History, Coins } from 'lucide-react';
import {
  useReferralCreditHistory,
  type CreditReason,
  type CreditHistoryRow,
} from '@/hooks/useReferralCreditHistory';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';

interface ReferralCreditHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 25;

/** Tradução amigável do tipo de movimento.
 *  Reasons reais gravados em `tenant_referral_credit_ledger`:
 *  consume, reverse, free_tier_grant, bonus_tier_activation, purchase, grant, bonus.
 *  `dot` = classe de bg da bolinha colorida exibida ao lado do label. */
function reasonLabel(reason: string): { label: string; dot: string } {
  switch (reason) {
    case 'consume':
      return { label: 'Consumo', dot: 'bg-destructive' };
    case 'reverse':
      return { label: 'Estorno', dot: 'bg-emerald-500' };
    case 'grant':
    case 'free_tier_grant':
      return { label: 'Cortesia', dot: 'bg-blue-500' };
    case 'purchase':
      return { label: 'Compra', dot: 'bg-violet-500' };
    case 'bonus':
    case 'bonus_tier_activation':
      return { label: 'Bônus', dot: 'bg-amber-500' };
    default:
      return { label: reason, dot: 'bg-muted-foreground' };
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBRL(value: number | null | undefined, currency = 'BRL'): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
}

export function ReferralCreditHistoryModal({
  open,
  onOpenChange,
}: ReferralCreditHistoryModalProps) {
  const isMobile = useIsMobile();
  const [page, setPage] = useState(0);
  const [reason, setReason] = useState<CreditReason | 'all'>('all');
  // v1.1.0: deep-link p/ DealDetailsModalPremium ao clicar na coluna Negócio
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

  const { data, isLoading } = useReferralCreditHistory({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    reason: reason === 'all' ? null : reason,
    enabled: open,
  });

  const rows: CreditHistoryRow[] = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const summary = useMemo(() => {
    const consumed = rows.filter((r) => r.reason === 'consume').length;
    const reversed = rows.filter((r) => r.reason === 'reverse').length;
    return { consumed, reversed };
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-5xl p-0 gap-0 overflow-hidden',
          isMobile && 'w-screen h-[100dvh] max-w-none rounded-none',
        )}
      >
        <DialogHeader className="px-5 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4 text-primary" />
            Histórico de Consumo de Créditos
          </DialogTitle>
          <DialogDescription className="text-xs">
            Cada conversão consome 1 crédito em todos os planos. Se o negócio for revertido para
            uma fase de perda, o crédito retorna automaticamente — exceto quando a comissão já
            foi paga ao afiliado.
          </DialogDescription>
        </DialogHeader>

        {/* Filtros */}
        <div className="px-5 py-3 border-b flex flex-wrap items-center gap-3 bg-muted/30">
          <Select value={reason} onValueChange={(v) => { setPage(0); setReason(v as any); }}>
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="consume">Consumo</SelectItem>
              <SelectItem value="reverse">Estorno</SelectItem>
              <SelectItem value="free_tier_grant">Cortesia (Free tier)</SelectItem>
              <SelectItem value="bonus_tier_activation">Bônus de plano</SelectItem>
              <SelectItem value="purchase">Compra</SelectItem>
              <SelectItem value="grant">Outros grants</SelectItem>
            </SelectContent>
          </Select>

          <div className="ml-auto text-xs text-muted-foreground flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Coins className="h-3 w-3" /> {total.toLocaleString('pt-BR')} movimentos
            </span>
            <span>{summary.consumed} consumos · {summary.reversed} estornos (página)</span>
          </div>
        </div>

        {/* Tabela */}
        <div className="flex-1 overflow-auto px-2 sm:px-5 py-2 max-h-[60vh]">
          {isLoading ? (
            <div className="space-y-2 py-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Nenhum movimento de crédito encontrado para os filtros selecionados.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Data</TableHead>
                  <TableHead className="w-[110px]">Tipo</TableHead>
                  <TableHead>Indicação</TableHead>
                  <TableHead>Negócio</TableHead>
                  <TableHead>Afiliado</TableHead>
                  <TableHead className="text-right w-[90px]">Crédito</TableHead>
                  <TableHead className="text-right w-[90px]">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, idx) => {
                  const lbl = reasonLabel(r.reason);
                  return (
                    <TableRow key={`${r.id_short}-${idx}`}>
                      <TableCell className="text-xs tabular-nums">{formatDate(r.created_at)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
                          <span className={cn('h-2 w-2 rounded-full shrink-0', lbl.dot)} aria-hidden />
                          {lbl.label}
                        </span>
                        {r.unbilled && (
                          <span className="ml-1 text-[10px] text-amber-600">unbilled</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.referral ? (
                          <div className="flex flex-col">
                            <span className="font-medium truncate max-w-[180px]">
                              {r.referral.lead_name || '—'}
                            </span>
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                              #{r.referral.id_short} · {r.referral.status}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.deal ? (
                          <button
                            type="button"
                            onClick={() => setSelectedDealId(r.deal!.id)}
                            className="flex flex-col text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm group"
                            aria-label={`Abrir negócio ${r.deal.title ?? r.deal.id_short}`}
                          >
                            <span className="font-medium truncate max-w-[180px] group-hover:text-primary transition-colors">
                              {r.deal.title || '—'}
                            </span>
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                              #{r.deal.id_short} · {formatBRL(r.deal.value)}
                            </span>
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.affiliate ? (
                          <div className="flex flex-col">
                            <span className="font-medium truncate max-w-[140px]">
                              {r.affiliate.full_name || '—'}
                            </span>
                            {r.commission && (
                              <span className="text-[10px] text-muted-foreground tabular-nums">
                                #{r.commission.id_short} · {formatBRL(r.commission.reward_value, r.commission.currency || 'BRL')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-xs font-medium">
                        <span className={cn(r.delta < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400')}>
                          {r.delta > 0 ? '+' : ''}{r.delta}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-xs">
                        {r.balance_after}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer paginação */}
        <div className="px-5 py-3 border-t flex items-center justify-between bg-muted/20">
          <span className="text-xs text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isLoading}
            >
              <ChevronLeft className="h-3 w-3" /> Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages || isLoading}
            >
              Próxima <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Modal de detalhes do negócio (deep-link da coluna Negócio) */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={!!selectedDealId}
        onOpenChange={(o) => { if (!o) setSelectedDealId(null); }}
      />
    </Dialog>
  );
}
